import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { SETTINGS_KEYS } from '../../config/environment'
import { HIERARCHY_PAGE_SIZE } from '../../constants/pagination'
import { useAuth } from '../../hooks/useAuth'
import { useCorrespondentsCache } from '../../hooks/useCorrespondentsCache'
import { useDocumentTypesCache } from '../../hooks/useDocumentTypesCache'
import { useStoragePathsCache } from '../../hooks/useStoragePathsCache'
import { useWorkflowStatusesCache } from '../../hooks/useWorkflowStatusesCache'
import { customFieldService } from '../../services/custom-field.service'
import { DisplayFieldEnum, DisplayMode, documentListViewService } from '../../services/document-list-view.service'
import { documentService } from '../../services/document.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { savedViewService } from '../../services/saved-view.service'
import { settingsService } from '../../services/settings.service'
import { taskService } from '../../services/task.service'
import { toastService } from '../../services/toast.service'
import { uploadService } from '../../services/upload.service'
import { userService } from '../../services/user.service'
import { websocketService } from '../../services/websocket.service'
import { useAppStore } from '../../store/useAppStore'
import { CustomField, FilterRule, SavedView, Tag as TagType } from '../../types'
import { formatCustomFieldDisplayValue } from '../../utils/customFieldDisplay'
import { formatDate } from '../../utils/dateFormatter'
import { getDocumentOwnerDisplayName } from '../../utils/documentOwnerDisplay'
import { parseBackendErrors } from '../../utils/errorHandler'
import { captureOrganizationScope, showErrorIfSameOrganization } from '../../utils/orgScopedNotification'
import { buildInputAcceptString, getEffectiveAllowedMimes } from '../../utils/uploadMimePolicy'
import ClearableBadge from '../common/ClearableBadge'
import ConfirmDialog from '../common/ConfirmDialog'
import { DocumentationDialog } from '../common/DocumentationDialog'
import IfPermissions from '../common/IfPermissions'
import PageHeader from '../common/PageHeader'
import Pagination from '../common/Pagination'
import PreviewPopup from '../common/PreviewPopup'
import SaveViewConfigDialog, { SaveViewConfig } from '../common/SaveViewConfigDialog'
import Tag from '../common/Tag'
import BulkEditor from './BulkEditor'
import CreateDocumentDialog from './CreateDocumentDialog'
import { shouldIgnoreClick } from './documentCardInteraction'
import DocumentCardLarge from './DocumentCardLarge'
import DocumentCardSmall from './DocumentCardSmall'
import { DocumentHierarchyView } from './DocumentHierarchyView'
import './DocumentList.scss'
import FilterEditor, { FILTER_HAS_STORAGE_PATH_ANY, FilterEditorRef } from './FilterEditor'

interface DocumentListProps {
  className?: string
}

const DocumentList: React.FC<DocumentListProps> = ({ className }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const params = useParams<{ id: string }>()
  const { isAuthenticated } = useAuth()
  const { t } = useTranslation()
  const canManageSavedViews =
    permissionsService.currentUserCan(PermissionAction.Display, PermissionType.SavedView) &&
    permissionsService.currentUserCan(PermissionAction.View, PermissionType.SavedView)

  const { correspondents: correspondentsCache, loaded: correspondentsCacheLoaded } = useCorrespondentsCache()
  const { documentTypes: documentTypesCache, loaded: documentTypesCacheLoaded } = useDocumentTypesCache()
  const { workflowStatuses: workflowStatusesCache, loaded: workflowStatusesCacheLoaded } = useWorkflowStatusesCache()
  const { storagePaths: storagePathsCache, loaded: storagePathsCacheLoaded } = useStoragePathsCache()
  const uiSettings = useAppStore((s) => s.uiSettings)
  const currentOrganizationId = uiSettings?.currentOrganizationId ?? null
  const uploadAcceptString = useMemo(() => {
    const supported = uiSettings?.settings?.supported_upload_types
    const allowed = uiSettings?.settings?.allowed_upload_mime_types
    const effective = getEffectiveAllowedMimes(supported, allowed)
    return buildInputAcceptString(effective, supported)
  }, [uiSettings])

  const [usersCache, setUsersCache] = useState<any[]>([])
  const [usersCacheLoaded, setUsersCacheLoaded] = useState(false)

  useEffect(() => {
    if (!usersCacheLoaded) {
      if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)) {
        setUsersCache([])
        setUsersCacheLoaded(true)
        return
      }
      userService
        .listAll()
        .then((response) => {
          setUsersCache(response.results || [])
          setUsersCacheLoaded(true)
        })
        .catch(() => {
          setUsersCacheLoaded(true)
        })
    }
  }, [usersCacheLoaded])

  // Check if we're on a document detail page (must be after hooks)
  const currentPath = location.pathname
  const isOnDocumentDetailPage = /^\/documents\/\d+/.test(currentPath)

  const [unmodifiedFilterRules, setUnmodifiedFilterRules] = useState<FilterRule[]>([])
  const [unmodifiedSavedView, setUnmodifiedSavedView] = useState<any>(null)
  const [rulesModified, setRulesModified] = useState(false)

  // Save view dialog state
  const [showSaveViewDialog, setShowSaveViewDialog] = useState(false)
  const [saveViewError, setSaveViewError] = useState<{ name?: string; filter_rules?: string } | undefined>()
  const [showDocsDialog, setShowDocsDialog] = useState(false)
  const [allSavedViews, setAllSavedViews] = useState<SavedView[]>([])
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false)
  const [showCreateDocumentDialog, setShowCreateDocumentDialog] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<any>(null)
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [filtersCollapsed, setFiltersCollapsed] = useState(() => {
    const isMobileInitial = window.innerWidth < 992
    if (isMobileInitial) {
      return false
    }
    const saved = localStorage.getItem('documentListFiltersCollapsed')
    return saved === 'true'
  })
  const filterEditorRef = useRef<FilterEditorRef>(null)
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() => {
    return window.innerWidth < 992
  })

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992
      setIsMobile(mobile)
      if (mobile && filtersCollapsed) {
        setFiltersCollapsed(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [filtersCollapsed])

  useEffect(() => {
    localStorage.setItem('documentListFiltersCollapsed', filtersCollapsed.toString())
  }, [filtersCollapsed])

  // Get state from service
  const [serviceState, setServiceState] = useState({
    documents: documentListViewService.documents,
    selected: documentListViewService.selected,
    filterRules: documentListViewService.filterRules,
    sortField: documentListViewService.sortField,
    sortReverse: documentListViewService.sortReverse,
    currentPage: documentListViewService.currentPage,
    pageSize: documentListViewService.pageSize,
    collectionSize: documentListViewService.collectionSize,
    displayMode: documentListViewService.displayMode,
    displayFields: documentListViewService.displayFields,
    selectionData: documentListViewService.selectionData,
    isReloading: documentListViewService.isReloading,
    hasLoadedOnce: documentListViewService.hasLoadedOnce,
    error: documentListViewService.error,
    activeSavedViewId: documentListViewService.activeSavedViewId,
    activeSavedViewTitle: documentListViewService.activeSavedViewTitle,
    updateCount: documentListViewService.updateCount,
  })

  // Update state when service changes
  // Učitaj sve saved views za dropdown
  useEffect(() => {
    // Don't load if on document detail page
    if (isOnDocumentDetailPage) return

    const loadAllSavedViews = async () => {
      if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.SavedView)) {
        setAllSavedViews([])
        return
      }
      try {
        const response = await savedViewService.getSavedViews({ page_size: 100 })
        setAllSavedViews(response.results)
      } catch (error) {}
    }
    void loadAllSavedViews()

    const loadCustomFields = async () => {
      if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.CustomField)) {
        setCustomFields([])
        return
      }
      try {
        const response = await customFieldService.listAll()
        setCustomFields(response.results || [])
      } catch (error) {}
    }
    void loadCustomFields()
  }, [isOnDocumentDetailPage, currentOrganizationId])

  useEffect(() => {
    // Don't subscribe if on document detail page
    if (isOnDocumentDetailPage) return

    const updateState = () => {
      // Double-check we're still on list route before updating state
      const currentPath = window.location.pathname
      if (/^\/documents\/\d+/.test(currentPath)) {
        return // Still on detail page, don't update
      }

      // IMPORTANT: ALWAYS use filterRules from URL if URL has filters
      // This prevents filters from being reset when service updates
      const currentUrlParams = new URLSearchParams(window.location.search)
      const urlParamsStr = currentUrlParams.toString()
      const hasUrlFilters =
        urlParamsStr !== '' &&
        urlParamsStr !== 'page=1' &&
        !urlParamsStr.match(/^(page=1&)?(sort=[^&]+(&reverse=1)?&?(page=\d+)?)?$/i) &&
        Array.from(currentUrlParams.keys()).some((key) => key !== 'page' && key !== 'sort' && key !== 'reverse')

      let filterRulesToUse = documentListViewService.filterRules
      if (hasUrlFilters) {
        const { filterRulesFromQueryParams } = require('../../utils/queryParams')
        const urlFilterRules = filterRulesFromQueryParams(currentUrlParams)
        filterRulesToUse = urlFilterRules
      }

      setServiceState({
        documents: documentListViewService.documents,
        selected: documentListViewService.selected,
        filterRules: filterRulesToUse,
        sortField: documentListViewService.sortField,
        sortReverse: documentListViewService.sortReverse,
        currentPage: documentListViewService.currentPage,
        pageSize: documentListViewService.pageSize,
        collectionSize: documentListViewService.collectionSize,
        displayMode: documentListViewService.displayMode,
        displayFields: documentListViewService.displayFields,
        selectionData: documentListViewService.selectionData,
        isReloading: documentListViewService.isReloading,
        hasLoadedOnce: documentListViewService.hasLoadedOnce,
        error: documentListViewService.error,
        activeSavedViewId: documentListViewService.activeSavedViewId,
        activeSavedViewTitle: documentListViewService.activeSavedViewTitle,
        updateCount: documentListViewService.updateCount,
      })
    }

    // Subscribe to service updates
    const unsubscribe = documentListViewService.subscribe(updateState)
    return unsubscribe
  }, [isOnDocumentDetailPage])

  const {
    documents,
    selected,
    filterRules,
    sortField,
    sortReverse,
    currentPage,
    pageSize,
    collectionSize,
    displayMode,
    displayFields,
    selectionData,
    isReloading,
    hasLoadedOnce,
    error,
    activeSavedViewId,
    activeSavedViewTitle,
  } = serviceState

  const isHierarchyView = displayMode === DisplayMode.HIERARCHY

  // Storage paths selected in the folder filter - narrows the hierarchy view to just those folders.
  const activeStoragePathIds = useMemo(
    () =>
      filterRules
        .filter((rule) => rule.rule_type === FILTER_HAS_STORAGE_PATH_ANY)
        .map((rule) => Number(rule.value))
        .filter((id) => id > 0),
    [filterRules]
  )

  // Equalize card heights - ensures all cards have same height
  useEffect(() => {
    if (displayMode !== DisplayMode.SMALL_CARDS || documents.length === 0) {
      return undefined
    }

    // Small delay to let cards render
    const timer = setTimeout(() => {
      const cardElements = Array.from(window.document.querySelectorAll('.document-card')) as HTMLElement[]
      if (cardElements.length === 0) return

      // Reset heights first
      cardElements.forEach((card) => {
        card.style.minHeight = ''
      })

      // Calculate max height
      let maxHeight = 0
      cardElements.forEach((card) => {
        const height = card.offsetHeight
        if (height > maxHeight) {
          maxHeight = height
        }
      })

      // Apply max height to all cards
      if (maxHeight > 0) {
        cardElements.forEach((card) => {
          card.style.minHeight = `${maxHeight}px`
        })
      }
    }, 150)

    return () => clearTimeout(timer)
  }, [displayMode, documents, displayFields])

  // Track navigation state
  const isNavigatingFromServiceRef = useRef(false)
  const lastProcessedLocationRef = useRef('')

  const loadViewConfig = useCallback(
    async (viewId: number) => {
      try {
        const view = await savedViewService.getCached(viewId)

        if (view) {
          setUnmodifiedSavedView(view)
          documentListViewService.activateSavedViewWithQueryParams(view, new URLSearchParams(location.search))
          setUnmodifiedFilterRules(view.filter_rules || [])
          await documentListViewService.reload()
        } else {
          navigate('/404')
        }
      } catch (error) {
        navigate('/404')
      }
    },
    [navigate, location.search]
  )

  const loadDocuments = useCallback(async () => {
    const currentPath = location.pathname
    if (/^\/documents\/\d+/.test(currentPath)) {
      return
    }
    if (currentOrganizationId === null) {
      return
    }

    const organizationSwitched = documentListViewService.isOrganizationSwitchPending()
    documentListViewService.setOrganizationContext(currentOrganizationId, {
      discardSavedFilters: organizationSwitched,
    })

    if (organizationSwitched) {
      if (location.search !== '' || params.id) {
        navigate('/documents', { replace: true })
        return
      }
      documentListViewService.clearPendingOrganizationSwitch()
    }

    // Handle /view/:id route (like React - paramMap)
    if (params.id) {
      await loadViewConfig(parseInt(params.id))
      return
    }

    // Handle URL query parameters
    const urlParams = new URLSearchParams(location.search)
    if (urlParams.get('view')) {
      await loadViewConfig(parseInt(urlParams.get('view')!))
    } else {
      documentListViewService.activateSavedView(null)

      documentListViewService.loadFromQueryParams(urlParams)

      const { filterRulesFromQueryParams } = require('../../utils/queryParams')
      const urlFilterRules = filterRulesFromQueryParams(urlParams)

      const hasActiveFilters = urlFilterRules.length > 0
      if (hasActiveFilters) {
        documentListViewService.filterRules = urlFilterRules
        setUnmodifiedFilterRules([])
      } else {
        setUnmodifiedFilterRules([])
      }

      try {
        // Reload će učitati dokumente sa filterima iz URL-a
        // The service now has the correct filterRules set above
        await documentListViewService.reload()
      } catch (error) {}
    }
  }, [location.pathname, location.search, params.id, loadViewConfig, navigate, currentOrganizationId])

  // Keep latest loadDocuments for the auth/pathname effect without listing
  // loadDocuments in that effect's deps: loadDocuments changes whenever
  // location.search changes, and the URL-change effect below already calls
  // loadDocuments for search updates — including both caused duplicate GETs.
  const loadDocumentsRef = useRef(loadDocuments)
  loadDocumentsRef.current = loadDocuments

  // Setup navigate callback only once on mount
  useEffect(() => {
    // Only set navigate callback if we're on the documents list route (not on document detail)
    // Check if pathname matches /documents without an ID (i.e., not /documents/:id or /documents/:id/:section)
    const isOnDocumentListRoute =
      location.pathname === '/documents' ||
      location.pathname.startsWith('/documents?') ||
      (location.pathname.startsWith('/documents') && !/^\/documents\/\d+/.test(location.pathname))

    if (!isOnDocumentListRoute) {
      // Don't set navigate callback if we're on a document detail page
      return
    }

    documentListViewService.setNavigateCallback((params) => {
      // Mark that we're navigating from service to prevent reload loop
      isNavigatingFromServiceRef.current = true

      // Double-check we're still on the list route before navigating
      const currentPath = window.location.pathname
      if (currentPath !== '/documents' && !/^\/documents\/view\/\d+$/.test(currentPath) && !currentPath.match(/^\/documents(\?|$)/)) {
        // We're not on the documents list route anymore, don't update URL
        isNavigatingFromServiceRef.current = false
        return
      }

      // Convert params to URLSearchParams format
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, value.toString())
        }
      })

      const searchString = searchParams.toString()
      const currentSearch = window.location.search.replace(/^\?/, '')

      // CRITICAL: Check if URL already has the same parameters
      // BUT: Allow removal of more_like_id when filterRules are empty (user reset filters)
      const currentParams = new URLSearchParams(currentSearch)
      const newParams = new URLSearchParams(searchString)

      // Compare all parameters to see if they match
      let paramsMatch = true
      if (currentParams.toString() !== newParams.toString()) {
        paramsMatch = false
      }

      if (currentParams.has('more_like_id') && !newParams.has('more_like_id')) {
        const serviceHasEmptyFilters = documentListViewService.filterRules.length === 0
        if (!serviceHasEmptyFilters) {
          isNavigatingFromServiceRef.current = false
          return
        }
      }

      if (paramsMatch && currentSearch === searchString) {
        isNavigatingFromServiceRef.current = false
        return
      }

      // Navigate with new query params - ensure we're on /documents pathname
      navigate(
        {
          pathname: '/documents',
          search: searchString,
        },
        { replace: true }
      )

      // Reset the flag after a delay - must be long enough for useEffect to run
      setTimeout(() => {
        isNavigatingFromServiceRef.current = false
      }, 300)
    })

    // Cleanup on unmount
    return () => {
      documentListViewService.setNavigateCallback(null)
      documentListViewService.cancelPending()
    }
  }, [navigate, location.pathname])

  // Initial load when authenticated
  useEffect(() => {
    // IMPORTANT: Don't load if we're on a document detail page
    const currentPath = location.pathname
    if (/^\/documents\/\d+/.test(currentPath)) {
      // We're on a document detail page - don't load document list
      return
    }

    if (isAuthenticated === true && currentOrganizationId !== null) {
      // Don't reset unmodifiedFilterRules here - it might overwrite filters from URL
      // Only reset if URL has no filters (handled in loadDocuments)
      void loadDocumentsRef.current()
    }
  }, [isAuthenticated, location.pathname, currentOrganizationId])

  // Handle URL changes (but not on initial load when triggered by service)
  // This is a separate effect so it doesn't interfere with initial load
  useEffect(() => {
    // IMPORTANT: Don't process URL changes if we're on a document detail page
    const currentPath = location.pathname
    if (/^\/documents\/\d+/.test(currentPath)) {
      // We're on a document detail page - don't process URL changes for document list
      return
    }

    // Skip initial processing - handled by isAuthenticated effect
    if (!lastProcessedLocationRef.current) {
      lastProcessedLocationRef.current = location.search + (params.id || '')
      return
    }

    // Skip if not authenticated
    if (isAuthenticated !== true) {
      return
    }

    // Skip if navigation came from service
    // The service will handle reload after URL update
    if (isNavigatingFromServiceRef.current) {
      lastProcessedLocationRef.current = location.search + (params.id || '')
      return
    }

    // Check if URL or route params changed
    const currentLocation = location.search + (params.id || '')
    if (lastProcessedLocationRef.current !== currentLocation) {
      lastProcessedLocationRef.current = currentLocation
      loadDocuments()
    }
  }, [location.pathname, location.search, params.id, isAuthenticated, loadDocuments])

  useEffect(() => {
    // Check if rules have been modified
    setRulesModified(JSON.stringify(filterRules) !== JSON.stringify(unmodifiedFilterRules))
  }, [filterRules, unmodifiedFilterRules])

  useEffect(() => {
    // Force re-render when service updates
  }, [serviceState.updateCount])

  // CRITICAL: Early return if we're on a document detail page
  // This component should only render on /documents list route, not /documents/:id routes
  // Must be after all hooks to satisfy React Hooks rules
  if (isOnDocumentDetailPage) {
    // Don't render DocumentList on document detail pages - DocumentDetail handles those routes
    return null
  }

  // Show loading if not authenticated yet
  if (isAuthenticated === null) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  const getTitle = () => {
    let title = activeSavedViewTitle
    if (title && savedViewIsModified()) {
      title += '*'
    } else if (!title) {
      title = t('documents')
    }
    return title
  }

  const savedViewIsModified = (): boolean => {
    if (!activeSavedViewId || !unmodifiedSavedView) return false

    return (
      unmodifiedSavedView.sort_field !== sortField ||
      unmodifiedSavedView.sort_reverse !== sortReverse ||
      (unmodifiedSavedView.page_size && unmodifiedSavedView.page_size !== pageSize) ||
      (unmodifiedSavedView.display_mode && unmodifiedSavedView.display_mode !== displayMode) ||
      (!unmodifiedSavedView.display_mode && displayMode !== DisplayMode.SMALL_CARDS) ||
      (unmodifiedSavedView.display_fields && unmodifiedSavedView.display_fields.join(',') !== displayFields.join(',')) ||
      (!unmodifiedSavedView.display_fields && displayFields.join(',') !== DisplayFieldEnum.CREATED.toString()) ||
      JSON.stringify(unmodifiedSavedView.filter_rules) !== JSON.stringify(filterRules)
    )
  }

  const getDisplayFieldLabel = (field: DisplayFieldEnum): string => {
    const labelMap: Record<DisplayFieldEnum, string> = {
      [DisplayFieldEnum.ASN]: t('asn'),
      [DisplayFieldEnum.CORRESPONDENT]: t('correspondent'),
      [DisplayFieldEnum.TITLE]: t('title'),
      [DisplayFieldEnum.TAGS]: t('tags'),
      [DisplayFieldEnum.OWNER]: t('owner'),
      [DisplayFieldEnum.NOTES]: t('notes'),
      [DisplayFieldEnum.DOCUMENT_TYPE]: t('document_type'),
      [DisplayFieldEnum.WORKFLOW_STATUS]: t('document_workflow.status'),
      [DisplayFieldEnum.STORAGE_PATH]: t('storage_path'),
      [DisplayFieldEnum.CREATED]: t('created'),
      [DisplayFieldEnum.ADDED]: t('added'),
      [DisplayFieldEnum.PAGE_COUNT]: t('pages'),
      [DisplayFieldEnum.SHARED]: t('shared'),
      [DisplayFieldEnum.CUSTOM_FIELD]: t('custom_fields'),
    }
    return labelMap[field] || field
  }

  const getSortFields = () => {
    const isFullText = filterRules.some((rule) => rule.rule_type === 20 || rule.rule_type === 21)

    let baseFields: Array<{ field: string; name: string }> = []

    if (isFullText) {
      baseFields = [
        { field: 'archive_serial_number', name: t('asn') },
        { field: 'correspondent__name', name: t('correspondent') },
        { field: 'title', name: t('title') },
        { field: 'tags__name', name: t('tags') },
        { field: 'content', name: t('content') },
        { field: 'owner', name: t('owner') },
        { field: 'document_type__name', name: t('document_type') },
        { field: 'workflow_status__name', name: t('document_workflow.status') },
        { field: 'storage_path__name', name: t('storage_path') },
        { field: 'created', name: t('created') },
        { field: 'added', name: t('added') },
        { field: 'page_count', name: t('pages') },
      ]
    } else {
      baseFields = [
        { field: 'archive_serial_number', name: t('asn') },
        { field: 'correspondent__name', name: t('correspondent') },
        { field: 'title', name: t('title') },
        { field: 'tags__name', name: t('tags') },
        { field: 'owner', name: t('owner') },
        { field: 'document_type__name', name: t('document_type') },
        { field: 'workflow_status__name', name: t('document_workflow.status') },
        { field: 'storage_path__name', name: t('storage_path') },
        { field: 'created', name: t('created') },
        { field: 'added', name: t('added') },
        { field: 'page_count', name: t('pages') },
      ]
    }

    const filteredFields = baseFields.filter((field) => {
      if (field.field === 'tags__name') {
        return permissionsService.currentUserCan(PermissionAction.View, PermissionType.Tag)
      }
      if (field.field === 'correspondent__name') {
        return permissionsService.currentUserCan(PermissionAction.View, PermissionType.Correspondent)
      }
      if (field.field === 'document_type__name') {
        return permissionsService.currentUserCan(PermissionAction.View, PermissionType.DocumentType)
      }
      if (field.field === 'workflow_status__name') {
        return permissionsService.currentUserCan(PermissionAction.View, PermissionType.WorkflowStatus)
      }
      if (field.field === 'owner') {
        return permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)
      }
      return true
    })

    if (permissionsService.currentUserCan(PermissionAction.View, PermissionType.CustomField)) {
      customFields.forEach((field) => {
        filteredFields.push({
          field: `custom_field_${field.id}`,
          name: field.name,
        })
      })
    }

    return filteredFields
  }

  const handleSort = (field: string) => {
    if (sortField !== field) {
      documentListViewService.setSort(field, false)
    } else if (sortField === field && !sortReverse) {
      documentListViewService.setSort(field, true)
    } else {
      documentListViewService.setSort('', false)
    }
  }

  const toggleDisplayField = (field: DisplayFieldEnum) => {
    const newFields = displayFields.includes(field) ? displayFields.filter((f) => f !== field) : [...displayFields, field]

    documentListViewService.displayFields = newFields
  }

  const getDisplayCustomFieldTitle = (fieldId: string): string => {
    const fieldIdNum = parseInt(fieldId.replace(DisplayFieldEnum.CUSTOM_FIELD, ''))
    const field = customFields.find((f) => f.id === fieldIdNum)
    return field ? field.name : fieldId
  }

  const activeDisplayCustomFields = displayFields.filter((field) => {
    if (typeof field !== 'string' || !field.startsWith(DisplayFieldEnum.CUSTOM_FIELD)) {
      return false
    }

    const fieldId = parseInt(field.replace(DisplayFieldEnum.CUSTOM_FIELD, ''))
    return customFields.some((customField) => customField.id === fieldId)
  })

  const openDocumentDetail = (document: any) => {
    navigate(`/documents/${document.id}/details`)
  }

  const handleDeleteClick = (document: any, e: React.MouseEvent) => {
    e.stopPropagation()
    setDocumentToDelete(document)
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async (permanentDelete?: boolean) => {
    if (!documentToDelete || isDeleteSubmitting) return
    try {
      setIsDeleteSubmitting(true)
      if (permanentDelete) {
        await documentService.bulkEditOperation([documentToDelete.id], 'delete', { permanent: true })
        toastService.showSuccess(
          t('document_permanently_deleted', { title: documentToDelete.title || documentToDelete.original_file_name || 'Untitled' }) ||
            t('document_deleted', { title: documentToDelete.title || documentToDelete.original_file_name || 'Untitled' })
        )
      } else {
        await documentService.deleteDocument(documentToDelete.id)
        toastService.showSuccess(
          t('document_moved_to_trash', { title: documentToDelete.title || documentToDelete.original_file_name || 'Untitled' }) ||
            t('document_deleted', { title: documentToDelete.title || documentToDelete.original_file_name || 'Untitled' })
        )
      }
      documentListViewService.reload()
      setShowDeleteDialog(false)
      setDocumentToDelete(null)
    } catch (error: any) {
      const message =
        error?.response?.status === 403 ? t('error_deleting_document_no_permission') : t('error_deleting_document') || 'Greška pri brisanju dokumenta'
      toastService.showError(message)
      setShowDeleteDialog(false)
      setDocumentToDelete(null)
    } finally {
      setIsDeleteSubmitting(false)
    }
  }

  const handleCreateDocumentMetadata = async (payload: {
    title?: string
    document_type: number
    correspondent?: number | null
    storage_path?: number | null
    tags?: number[]
    custom_fields?: Record<number, any>
    template_id?: number | null
  }) => {
    const response = await uploadService.createDocumentMetadata({
      title: payload.title,
      document_type: payload.document_type,
      correspondent: payload.correspondent,
      storage_path: payload.storage_path,
      tags: payload.tags,
      custom_fields: payload.custom_fields,
      template_id: payload.template_id,
    })
    let documentId = response.document_id
    if (payload.template_id && response.task_id) {
      websocketService.markTaskForAutoOpen(response.task_id)
    }
    // Template-based documents are rendered and consumed asynchronously. Wait
    // for the consume task to expose the resulting document id so the newly
    // created document can be opened immediately.
    if (!documentId && response.task_id) {
      for (let attempt = 0; attempt < 30; attempt += 1) {
        try {
          const tasks = await taskService.getTasks({ task_id: response.task_id, page_size: 10 })
          const task = tasks.find((item) => item.task_id === response.task_id)
          if (task?.related_document) {
            documentId = Number(task.related_document)
            break
          }
          if (task?.status === 'FAILURE') break
        } catch {
          break
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1000))
      }
    }

    // Refresh the collection before navigating so the document is visible
    // immediately when the user returns to the list.
    await documentListViewService.reload()
    toastService.showSuccess(
      documentId ? t('document_created') || 'Dokument je kreiran.' : t('document_upload_started') || 'Kreiranje dokumenta je pokrenuto.'
    )
    if (documentId) {
      navigate(`/documents/${documentId}/details`)
    }
  }

  const canDelete = permissionsService.currentUserCan(PermissionAction.Delete, PermissionType.Document)

  const toggleSelected = (document: any, event: React.MouseEvent) => {
    if (!event.shiftKey) {
      documentListViewService.toggleSelected(document)
    } else {
      documentListViewService.selectRangeTo(document)
    }
  }

  const handleCardClick = (document: any, event: React.MouseEvent) => {
    if (shouldIgnoreClick(event)) {
      return
    }

    if (event.shiftKey) {
      documentListViewService.selectRangeTo(document)
    } else if (event.ctrlKey || event.metaKey) {
      documentListViewService.toggleSelected(document)
    } else {
      openDocumentDetail(document)
    }
  }

  const clickTag = (tagId: number) => {
    documentListViewService.selectNone()
    filterEditorRef.current?.toggleTag(tagId)
  }

  const clickCorrespondent = (correspondentId: number) => {
    documentListViewService.selectNone()
    filterEditorRef.current?.toggleCorrespondent(correspondentId)
  }

  const clickDocumentType = (documentTypeId: number) => {
    documentListViewService.selectNone()
    filterEditorRef.current?.toggleDocumentType(documentTypeId)
  }

  const clickWorkflowStatus = (workflowStatusId: number) => {
    documentListViewService.selectNone()
    filterEditorRef.current?.toggleWorkflowStatus(workflowStatusId)
  }

  const clickStoragePath = (storagePathId: number) => {
    documentListViewService.selectNone()
    filterEditorRef.current?.toggleStoragePath(storagePathId)
  }

  const clickMoreLike = (documentId: number) => {
    documentListViewService.quickFilter([{ rule_type: 'more_like', value: documentId.toString() }])
  }

  const resetFilters = () => {
    // Clear filters and reset page
    documentListViewService.filterRules = []
    setUnmodifiedFilterRules([])
    documentListViewService.currentPage = 1

    // Update URL immediately to remove filter parameters
    // Mark navigation as coming from service to prevent reload loop
    isNavigatingFromServiceRef.current = true
    documentListViewService.updateQueryParams()

    // Reset the flag after navigation completes
    setTimeout(() => {
      isNavigatingFromServiceRef.current = false
    }, 100)

    // Reload after URL is updated - _performReload will read filters from updated URL
    setTimeout(() => {
      documentListViewService.reload()
    }, 100)
  }

  const handleFilterRulesChange = (rules: FilterRule[]) => {
    documentListViewService.filterRules = rules
    documentListViewService.currentPage = 1
    documentListViewService.reload()
  }

  const handleSaveCurrentView = () => {
    if (activeSavedViewId) {
      // Update existing view
      handleSaveView(
        {
          name: activeSavedViewTitle || '',
          showInSideBar: unmodifiedSavedView?.show_in_sidebar || false,
          showOnDashboard: unmodifiedSavedView?.show_on_dashboard || false,
        },
        activeSavedViewId
      )
    } else {
      // Show dialog for new view
      setSaveViewError(undefined)
      setShowSaveViewDialog(true)
    }
  }

  const handleSaveAsNewView = () => {
    setSaveViewError(undefined)
    setShowSaveViewDialog(true)
  }

  const handleSaveView = async (config: SaveViewConfig, viewId?: number) => {
    try {
      const filteredDisplayFields = displayFields.filter((field) => {
        const fieldStr = field as string
        if (fieldStr === 'custom_field' || fieldStr === 'custom_field_') {
          return false
        }
        if (fieldStr === DisplayFieldEnum.CUSTOM_FIELD) {
          return false
        }
        return true
      })

      const savedView: any = {
        name: config.name,
        show_on_dashboard: config.showOnDashboard,
        show_in_sidebar: config.showInSideBar,
        filter_rules: filterRules,
        sort_reverse: sortReverse,
        sort_field: sortField,
        display_mode: displayMode,
        display_fields: filteredDisplayFields,
      }

      if (viewId) {
        // Update existing view
        await savedViewService.updateSavedView(viewId, savedView)
        toastService.showInfo(t('view_updated', { name: config.name }) || `Pregled "${config.name}" je ažuriran.`)
        setUnmodifiedSavedView({ ...savedView, id: viewId })
      } else {
        // Create new view
        const created = await savedViewService.createSavedView(savedView)
        toastService.showInfo(t('view_created', { name: config.name }) || `Pregled "${config.name}" je kreiran.`)
        // Optionally activate the new view
        documentListViewService.activeSavedViewId = created.id
        documentListViewService.activeSavedViewTitle = created.name
      }

      setShowSaveViewDialog(false)
      setSaveViewError(undefined)
    } catch (error: any) {
      const { fieldErrors, generalMessage } = parseBackendErrors(error, t, t('error_saving_view') || 'Greška prilikom čuvanja pogleda')
      const filterRulesError = Object.entries(fieldErrors)
        .filter(([key]) => key === 'filter_rules' || key.startsWith('filter_rules'))
        .map(([, message]) => message)
        .join(', ')
      setSaveViewError({
        name: fieldErrors.name,
        filter_rules: filterRulesError || undefined,
      })
      toastService.showError(generalMessage || t('error_saving_view') || 'Greška prilikom čuvanja pogleda')
    }
  }

  // Helper function to get correspondent name from ID or object
  const getCorrespondentName = (correspondent: number | any | null | undefined, correspondentDisplay?: { name?: string } | null): string => {
    if (!correspondent) return ''

    if (typeof correspondent === 'object' && correspondent.name) {
      return correspondent.name
    }

    if (correspondentDisplay?.name) {
      return correspondentDisplay.name
    }

    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Correspondent)) {
      return t('private')
    }

    const correspondentId = typeof correspondent === 'number' ? correspondent : null
    if (!correspondentId) return ''

    if (!correspondentsCacheLoaded || correspondentsCache.length === 0) {
      return ''
    }

    const corr = correspondentsCache.find((c: any) => c.id === correspondentId)
    if (!corr) {
      return t('private')
    }

    return corr.name || t('private')
  }

  // Helper function to get document type name from ID or object
  const getWorkflowStatusName = (workflowStatus: number | any | null | undefined): string => {
    if (!workflowStatus) return ''
    if (typeof workflowStatus === 'object' && workflowStatus.name) return workflowStatus.name
    const id = typeof workflowStatus === 'number' ? workflowStatus : null
    if (!id || !workflowStatusesCacheLoaded) return ''
    const ws = workflowStatusesCache.find((s: any) => s.id === id)
    return ws?.name ?? ''
  }

  const getDocumentTypeName = (documentType: number | any | null | undefined, documentTypeDisplay?: { name?: string } | null): string => {
    if (!documentType) return ''

    if (typeof documentType === 'object' && documentType.name) {
      return documentType.name
    }

    if (documentTypeDisplay?.name) {
      return documentTypeDisplay.name
    }

    const documentTypeId = typeof documentType === 'number' ? documentType : null
    if (!documentTypeId) return ''

    if (!documentTypesCacheLoaded || documentTypesCache.length === 0) {
      return ''
    }

    const dt = documentTypesCache.find((d: any) => d.id === documentTypeId)
    return dt?.name || ''
  }

  // Helper function to get storage path name from ID or object
  const getStoragePathName = (storagePath: number | any | null | undefined, storagePathDisplay?: { name?: string } | null): string => {
    if (storagePathDisplay?.name) {
      return storagePathDisplay.name
    }

    if (!storagePath) return ''

    if (typeof storagePath === 'object' && storagePath.name) {
      return storagePath.name
    }

    const storagePathId = typeof storagePath === 'number' ? storagePath : null
    if (!storagePathId) return ''

    if (!storagePathsCacheLoaded || storagePathsCache.length === 0) {
      return ''
    }

    const sp = storagePathsCache.find((s: any) => s.id === storagePathId)
    return sp?.name || ''
  }

  const handlePageChange = (page: number) => {
    documentListViewService.currentPage = page
    documentListViewService.reload()
  }

  const renderPagination = () => {
    const totalPages = Math.ceil(collectionSize / pageSize)
    const isTruncated = isHierarchyView && collectionSize > HIERARCHY_PAGE_SIZE

    return (
      <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          {isReloading && (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              <span>{t('loading')}</span>
            </>
          )}
          {selected.size > 0 && (
            <span>
              {collectionSize === 1
                ? `${t('selected')} ${selected.size} ${t('of')} 1 ${t('document')}`
                : `${t('selected')} ${selected.size} ${t('of')} ${collectionSize || 0} ${t('documents_2')}`}
            </span>
          )}
          {!isReloading && hasLoadedOnce && selected.size === 0 && (
            <>
              <span>{collectionSize === 1 ? `1 ${t('document')}` : `${collectionSize || 0} ${t('documents_2')}`}</span>
              {rulesModified && <span>&nbsp;{t('filtered')}</span>}
            </>
          )}
          {!isReloading && rulesModified && !activeSavedViewId && (
            <button className="btn btn-link py-0" onClick={resetFilters}>
              <i className="bi bi-x" style={{ width: '1em', height: '1em' }}></i>
              <small>{t('reset_filters')}</small>
            </button>
          )}
          {!isReloading && selected.size > 0 && (
            <button className="btn btn-link py-0" onClick={() => documentListViewService.selectNone()}>
              <i className="bi bi-slash-circle me-1" style={{ width: '1em', height: '1em' }}></i>
              <small>{t('clear_selection')}</small>
            </button>
          )}
          {!isReloading && isTruncated && (
            <span className="text-muted ms-2">
              <i className="bi bi-exclamation-triangle me-1"></i>
              <small>{t('hierarchy_limit_notice', { shown: HIERARCHY_PAGE_SIZE, total: collectionSize })}</small>
            </span>
          )}
        </div>
        {!isHierarchyView && collectionSize > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} maxVisiblePages={5} />
        )}
      </div>
    )
  }

  const openMobileFilters = () => setIsMobileFiltersOpen(true)
  const closeMobileFilters = () => setIsMobileFiltersOpen(false)

  return (
    <div className={`document-list ${className || ''}`}>
      <PageHeader title={getTitle()}>
        {/* Info Button - Mobile */}
        <div className="btn-group d-sm-none me-2">
          <Button variant="link" size="sm" className="documents-toolbar__info" onClick={() => setShowDocsDialog(true)} title={t('help')}>
            <i className="bi bi-info-circle"></i>
          </Button>
        </div>

        {/* Upload Button - Mobile */}
        <IfPermissions action={PermissionAction.Add} type={PermissionType.Document}>
          <div className="btn-group d-sm-none me-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="visually-hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files || [])
                if (files.length === 0) return

                toastService.showInfo(t('initiating_upload'), 3000)
                const suppressOnDashboard = settingsService.get(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUPPRESS_ON_DASHBOARD)
                navigate(suppressOnDashboard ? '/tasks?tab=completed' : '/dashboard')
                const uploadOrgId = captureOrganizationScope()

                for (const file of files) {
                  try {
                    await uploadService.uploadDocument(file)
                  } catch (error: any) {
                    showErrorIfSameOrganization(uploadOrgId, `${t('error_uploading_document')} "${file.name}": ${error.message || error}`, error)
                  }
                }

                if (fileInputRef.current) {
                  fileInputRef.current.value = ''
                }
              }}
              accept={uploadAcceptString}
            />
            <Button variant="outline-primary" size="sm" onClick={() => fileInputRef.current?.click()} className="d-inline-flex align-items-center">
              <i className="bi bi-cloud-upload"></i>
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowCreateDocumentDialog(true)}
              className="d-inline-flex align-items-center"
            >
              <i className="bi bi-plus-lg"></i>
            </Button>
          </div>
        </IfPermissions>

        {/* Mobile select dropdown */}
        <Dropdown className="btn-group flex-fill d-sm-none">
          <Dropdown.Toggle variant="outline-primary" size="sm" id="dropdownSelectMobile">
            <i className="bi bi-text-indent-left"></i>
            <div className="d-none d-sm-inline">&nbsp;{t('select')}</div>
            {selected.size > 0 && <ClearableBadge selected={true} number={selected.size} onCleared={() => documentListViewService.selectNone()} />}
          </Dropdown.Toggle>
          <Dropdown.Menu className="shadow">
            <Dropdown.Item onClick={() => documentListViewService.selectNone()}>{t('select_none')}</Dropdown.Item>
            <Dropdown.Item onClick={() => documentListViewService.selectPage()}>{t('select_page')}</Dropdown.Item>
            <Dropdown.Item onClick={() => documentListViewService.selectAll()}>{t('select_all')}</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        {/* Info Button - Desktop */}
        <div className="d-none d-sm-flex me-2">
          <Button variant="link" size="sm" className="documents-toolbar__info" onClick={() => setShowDocsDialog(true)} title={t('help')}>
            <i className="bi bi-info-circle"></i>
          </Button>
        </div>

        {/* Upload Button - Desktop */}
        <IfPermissions action={PermissionAction.Add} type={PermissionType.Document}>
          <div className="d-none d-sm-flex me-3 gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="visually-hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files || [])
                if (files.length === 0) return

                toastService.showInfo(t('initiating_upload'), 3000)
                const suppressOnDashboard = settingsService.get(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUPPRESS_ON_DASHBOARD)
                navigate(suppressOnDashboard ? '/tasks?tab=completed' : '/dashboard')
                const uploadOrgId = captureOrganizationScope()

                for (const file of files) {
                  try {
                    await uploadService.uploadDocument(file)
                  } catch (error: any) {
                    showErrorIfSameOrganization(uploadOrgId, `${t('error_uploading_document')} "${file.name}": ${error.message || error}`, error)
                  }
                }

                if (fileInputRef.current) {
                  fileInputRef.current.value = ''
                }
              }}
              accept={uploadAcceptString}
            />
            <Button variant="outline-primary" size="sm" onClick={() => fileInputRef.current?.click()} className="d-inline-flex align-items-center">
              <i className="bi bi-cloud-upload me-1"></i>
              {t('document')}
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowCreateDocumentDialog(true)}
              className="d-inline-flex align-items-center"
            >
              <i className="bi bi-plus-lg me-1"></i>
              {t('create')}
            </Button>
          </div>
        </IfPermissions>

        {/* Desktop select buttons */}
        <div className="d-none d-sm-flex flex-fill me-3">
          <div className="input-group input-group-sm">
            <span className="input-group-text border-0">{t('select_1')}</span>
          </div>
          <div className="btn-group btn-group-sm flex-nowrap">
            {selected.size > 0 && (
              <button
                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
                onClick={() => documentListViewService.selectNone()}
              >
                <i className="bi bi-slash-circle me-1"></i>
                {t('none')}
              </button>
            )}
            <button className="btn btn-sm btn-outline-primary d-inline-flex align-items-center" onClick={() => documentListViewService.selectPage()}>
              <i className="bi bi-file-earmark-check me-1"></i>
              {t('page')}
            </button>
            <button className="btn btn-sm btn-outline-primary d-inline-flex align-items-center" onClick={() => documentListViewService.selectAll()}>
              <i className="bi bi-check-all me-1"></i>
              {t('all')}
            </button>
          </div>
        </div>

        {/* Display fields dropdown */}
        <Dropdown className="btn-group flex-fill">
          <Dropdown.Toggle variant="outline-primary" size="sm" id="dropdownDisplayFields" className="d-inline-flex align-items-center">
            <i className="bi bi-card-heading"></i>
            <span className="d-none d-sm-inline ms-1">{t('show')}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="shadow">
            <div className="px-3">
              {Object.values(DisplayFieldEnum)
                .filter((field) => {
                  if (field === DisplayFieldEnum.CUSTOM_FIELD) return false
                  if (field === DisplayFieldEnum.CORRESPONDENT) {
                    return permissionsService.currentUserCan(PermissionAction.View, PermissionType.Correspondent)
                  }
                  if (field === DisplayFieldEnum.DOCUMENT_TYPE) {
                    return permissionsService.currentUserCan(PermissionAction.View, PermissionType.DocumentType)
                  }
                  if (field === DisplayFieldEnum.STORAGE_PATH) {
                    return permissionsService.currentUserCan(PermissionAction.View, PermissionType.StoragePath)
                  }
                  if (field === DisplayFieldEnum.OWNER) {
                    return permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)
                  }
                  if (field === DisplayFieldEnum.NOTES) {
                    return settingsService.get('notes_enabled')
                  }
                  return true
                })
                .map((field) => (
                  <div key={field} className="form-check my-1">
                    <input
                      className="form-check-input mt-1"
                      type="checkbox"
                      id={`displayField${field}`}
                      checked={displayFields.includes(field as DisplayFieldEnum)}
                      onChange={() => toggleDisplayField(field as DisplayFieldEnum)}
                    />
                    <label className="form-check-label" htmlFor={`displayField${field}`}>
                      {getDisplayFieldLabel(field as DisplayFieldEnum)}
                    </label>
                  </div>
                ))}
              {permissionsService.currentUserCan(PermissionAction.View, PermissionType.CustomField) &&
                customFields.map((field) => {
                  const fieldId = `${DisplayFieldEnum.CUSTOM_FIELD}${field.id}` as DisplayFieldEnum
                  return (
                    <div key={fieldId} className="form-check my-1">
                      <input
                        className="form-check-input mt-1"
                        type="checkbox"
                        id={`displayField${fieldId}`}
                        checked={displayFields.includes(fieldId)}
                        onChange={() => toggleDisplayField(fieldId)}
                      />
                      <label className="form-check-label" htmlFor={`displayField${fieldId}`}>
                        {field.name}
                      </label>
                    </div>
                  )
                })}
            </div>
          </Dropdown.Menu>
        </Dropdown>

        {/* Display mode buttons */}
        <div className="btn-group flex-fill" role="group">
          <input
            type="radio"
            className="btn-check"
            name="displayMode"
            id="displayModeDetails"
            checked={displayMode === DisplayMode.TABLE}
            onChange={() => (documentListViewService.displayMode = DisplayMode.TABLE)}
          />
          <label htmlFor="displayModeDetails" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center">
            <i className="bi bi-list-ul"></i>
          </label>

          <input
            type="radio"
            className="btn-check"
            name="displayMode"
            id="displayModeSmall"
            checked={displayMode === DisplayMode.SMALL_CARDS}
            onChange={() => (documentListViewService.displayMode = DisplayMode.SMALL_CARDS)}
          />
          <label htmlFor="displayModeSmall" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center">
            <i className="bi bi-grid"></i>
          </label>

          <input
            type="radio"
            className="btn-check"
            name="displayMode"
            id="displayModeLarge"
            checked={displayMode === DisplayMode.LARGE_CARDS}
            onChange={() => (documentListViewService.displayMode = DisplayMode.LARGE_CARDS)}
          />
          <label htmlFor="displayModeLarge" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center">
            <i className="bi bi-hdd-stack"></i>
          </label>

          <input
            type="radio"
            className="btn-check"
            name="displayMode"
            id="displayModeHierarchy"
            checked={displayMode === DisplayMode.HIERARCHY}
            onChange={() => (documentListViewService.displayMode = DisplayMode.HIERARCHY)}
          />
          <label htmlFor="displayModeHierarchy" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center">
            <i className="bi bi-diagram-3"></i>
          </label>
        </div>

        {/* Sort dropdown */}
        <Dropdown className="btn-group flex-fill">
          <Dropdown.Toggle variant="outline-primary" size="sm" id="dropdownBasic1" className="d-inline-flex align-items-center">
            <i className="bi bi-arrow-down-up"></i>
            <span className="d-none d-sm-inline ms-1">{t('sort')}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="shadow dropdown-menu-right">
            <div className="w-100 d-flex pb-2 mb-1 border-bottom">
              <input
                type="radio"
                className="btn-check"
                name="sortReverse"
                id="sortReverseFalse"
                checked={!sortReverse}
                onChange={() => (documentListViewService.sortReverse = false)}
              />
              <label htmlFor="sortReverseFalse" className="btn btn-outline-primary btn-sm mx-2 flex-fill">
                <i className="bi bi-sort-alpha-down"></i>
              </label>

              <input
                type="radio"
                className="btn-check"
                name="sortReverse"
                id="sortReverseTrue"
                checked={sortReverse}
                onChange={() => (documentListViewService.sortReverse = true)}
              />
              <label htmlFor="sortReverseTrue" className="btn btn-outline-primary btn-sm me-2 flex-fill">
                <i className="bi bi-sort-alpha-up-alt"></i>
              </label>
            </div>
            {getSortFields().map((field) => (
              <Dropdown.Item key={field.field} onClick={() => handleSort(field.field)} active={sortField === field.field}>
                {field.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        {/* Views dropdown */}
        <Dropdown className="btn-group flex-fill">
          <Dropdown.Toggle variant="outline-primary" size="sm" className="d-inline-flex align-items-center">
            <i className="bi bi-window-stack"></i>
            <span className="d-none d-sm-inline ms-1">{t('views')}</span>
            {savedViewIsModified() && (
              <div className="position-absolute top-0 start-100 p-2 translate-middle badge bg-secondary border border-light rounded-circle">
                <span className="visually-hidden">selected</span>
              </div>
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu className="shadow dropdown-menu-right">
            {/* Lista svih saved views - samo ako NIJE aktiviran nijedan */}
            {!activeSavedViewId && (
              <>
                {allSavedViews.map((view) => (
                  <Dropdown.Item key={view.id} onClick={() => loadViewConfig(view.id)}>
                    {view.name}
                  </Dropdown.Item>
                ))}
                {allSavedViews.length > 0 && <Dropdown.Divider />}
              </>
            )}

            {/* Save trenutnog view-a - samo ako JE aktiviran */}
            {activeSavedViewId && (
              <Dropdown.Item onClick={handleSaveCurrentView} disabled={!savedViewIsModified()}>
                {t('save')} "{activeSavedViewTitle}"
              </Dropdown.Item>
            )}

            {/* Save as... - uvek dostupno */}
            <Dropdown.Item onClick={handleSaveAsNewView}>{t('save_as')}</Dropdown.Item>

            {canManageSavedViews && (
              <>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => navigate('/savedviews')}>{t('all_saved_views')}</Dropdown.Item>
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </PageHeader>

      <div className={`documents-layout ${filtersCollapsed ? 'filters-collapsed' : ''}`}>
        <aside className={`documents-filters-panel ${filtersCollapsed ? 'is-collapsed' : ''} ${isMobileFiltersOpen ? 'is-open' : ''}`}>
          <FilterEditor
            ref={filterEditorRef}
            disabled={selected.size > 0}
            filterRules={filterRules}
            unmodifiedFilterRules={unmodifiedFilterRules}
            selectionData={selectionData}
            onFilterRulesChange={handleFilterRulesChange}
            onRequestClose={closeMobileFilters}
            collapsed={isMobile ? false : filtersCollapsed}
            onToggleCollapse={isMobile ? undefined : () => setFiltersCollapsed((prev) => !prev)}
          />
        </aside>

        <div className="documents-content">
          <div className="documents-filters-trigger d-lg-none">
            <button type="button" className="btn btn-outline-primary w-100" onClick={openMobileFilters}>
              <i className="bi bi-funnel me-2"></i>
              {t('filters')}
            </button>
          </div>

          {selected.size > 0 && (
            <div className="documents-bulk-editor">
              <BulkEditor />
            </div>
          )}

          <div className="documents-content__section">{renderPagination()}</div>

          {error && (
            <Alert variant="danger" role="alert">
              Error while loading documents: {error}
            </Alert>
          )}

          {!hasLoadedOnce && !error && (
            <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5">
              <div className="spinner-border mb-2" role="status"></div>
              <span>{t('loading')}</span>
            </div>
          )}

          {hasLoadedOnce && displayMode === DisplayMode.LARGE_CARDS && (
            <div>
              {documents.map((document) => (
                <DocumentCardLarge
                  key={document.id}
                  selected={documentListViewService.isSelected(document)}
                  document={document}
                  displayFields={displayFields}
                  usersCache={usersCache}
                  usersCacheLoaded={usersCacheLoaded}
                  onToggleSelected={toggleSelected}
                  onCardClick={handleCardClick}
                  onDoubleClickDocument={openDocumentDetail}
                  onClickTag={clickTag}
                  onClickMoreLike={clickMoreLike}
                  onClickCorrespondent={clickCorrespondent}
                  onClickDocumentType={clickDocumentType}
                  onClickWorkflowStatus={clickWorkflowStatus}
                  onClickStoragePath={clickStoragePath}
                />
              ))}
            </div>
          )}

          {hasLoadedOnce && displayMode === DisplayMode.SMALL_CARDS && (
            <div className="row row-cols-docdot-cards">
              {documents.map((document) => (
                <DocumentCardSmall
                  key={document.id}
                  selected={documentListViewService.isSelected(document)}
                  document={document}
                  displayFields={displayFields}
                  usersCache={usersCache}
                  usersCacheLoaded={usersCacheLoaded}
                  onToggleSelected={toggleSelected}
                  onCardClick={handleCardClick}
                  onDoubleClickDocument={openDocumentDetail}
                  onClickTag={clickTag}
                  onClickCorrespondent={clickCorrespondent}
                  onClickDocumentType={clickDocumentType}
                  onClickStoragePath={clickStoragePath}
                  onClickWorkflowStatus={clickWorkflowStatus}
                />
              ))}
            </div>
          )}

          {hasLoadedOnce && displayMode === DisplayMode.HIERARCHY && (
            <DocumentHierarchyView
              documents={documents}
              displayFields={displayFields}
              selected={selected}
              onToggleSelected={toggleSelected}
              onCardClick={handleCardClick}
              includeAllFolders
              activeStoragePathIds={activeStoragePathIds}
            />
          )}

          {hasLoadedOnce && displayMode === DisplayMode.TABLE && (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th></th>
                    {displayFields.includes(DisplayFieldEnum.TITLE) && (
                      <th
                        className={`sortable table-title-column ${sortField === 'title' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('title')}
                        title={t('sort_by_title')}
                      >
                        {t('title')}
                        {sortField === 'title' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.TAGS) && (
                      <th
                        className={`sortable ${sortField === 'tags__name' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('tags__name')}
                        title={t('sort_by_tags')}
                      >
                        {t('tags')}
                        {sortField === 'tags__name' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.ASN) && (
                      <th
                        className={`sortable ${sortField === 'archive_serial_number' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('archive_serial_number')}
                        title={t('sort_by_asn')}
                      >
                        {t('asn')}
                        {sortField === 'archive_serial_number' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.CORRESPONDENT) && (
                      <th
                        className={`sortable ${sortField === 'correspondent__name' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('correspondent__name')}
                        title={t('sort_by_correspondent')}
                      >
                        {t('correspondent')}
                        {sortField === 'correspondent__name' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.OWNER) && (
                      <th
                        className={`sortable ${sortField === 'owner' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('owner')}
                        title={t('sort_by_owner')}
                      >
                        {t('owner')}
                        {sortField === 'owner' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.NOTES) && (
                      <th
                        className={`sortable ${sortField === 'num_notes' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('num_notes')}
                        title={t('sort_by_notes')}
                      >
                        {t('notes')}
                        {sortField === 'num_notes' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.DOCUMENT_TYPE) && (
                      <th
                        className={`sortable ${sortField === 'document_type__name' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('document_type__name')}
                        title={t('sort_by_document_type')}
                      >
                        {t('document_type')}
                        {sortField === 'document_type__name' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.WORKFLOW_STATUS) && (
                      <th
                        className={`sortable ${sortField === 'workflow_status__name' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('workflow_status__name')}
                        title={t('sort_by_workflow_status')}
                      >
                        {t('document_workflow.status')}
                        {sortField === 'workflow_status__name' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.STORAGE_PATH) && (
                      <th
                        className={`sortable ${sortField === 'storage_path__name' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('storage_path__name')}
                        title={t('sort_by_storage_path')}
                      >
                        {t('storage_path')}
                        {sortField === 'storage_path__name' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.CREATED) && (
                      <th
                        className={`sortable ${sortField === 'created' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('created')}
                        title={t('sort_by_created_date')}
                      >
                        {t('created')}
                        {sortField === 'created' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.ADDED) && (
                      <th
                        className={`sortable ${sortField === 'added' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('added')}
                        title={t('sort_by_added_date')}
                      >
                        {t('added')}
                        {sortField === 'added' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.PAGE_COUNT) && (
                      <th
                        className={`sortable ${sortField === 'page_count' ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort('page_count')}
                        title={t('sort_by_pages')}
                      >
                        {t('pages')}
                        {sortField === 'page_count' && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    )}
                    {displayFields.includes(DisplayFieldEnum.SHARED) && <th>{t('shared')}</th>}
                    {activeDisplayCustomFields.map((fieldId) => (
                      <th
                        key={fieldId}
                        className={`sortable ${sortField === fieldId ? (sortReverse ? 'des' : 'asc') : ''}`}
                        onClick={() => handleSort(fieldId)}
                        title={t('sort_by', { name: getDisplayCustomFieldTitle(fieldId) })}
                      >
                        {getDisplayCustomFieldTitle(fieldId)}
                        {sortField === fieldId && <i className={`bi bi-arrow-${sortReverse ? 'up' : 'down'} ms-1`}></i>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {documents.map((document) => (
                    <tr
                      key={document.id}
                      onClick={(e) => {
                        if (shouldIgnoreClick(e)) {
                          return
                        }
                        toggleSelected(document, e)
                      }}
                      onDoubleClick={() => openDocumentDetail(document)}
                      className={documentListViewService.isSelected(document) ? 'table-row-selected' : ''}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex align-items-center gap-2">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`docCheck${document.id}`}
                              checked={documentListViewService.isSelected(document)}
                              onChange={() => {}}
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleSelected(document, e)
                              }}
                            />
                            <label className="form-check-label" htmlFor={`docCheck${document.id}`} onClick={(e) => e.stopPropagation()}></label>
                          </div>
                          {canDelete && (
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger p-0 table-delete-btn"
                              onClick={(e) => handleDeleteClick(document, e)}
                              title={t('delete_document')}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                      {displayFields.includes(DisplayFieldEnum.TITLE) && (
                        <td className="table-title-column">
                          <div className="d-flex align-items-center flex-wrap">
                            <button
                              className="btn btn-link p-0 text-start document-title"
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                openDocumentDetail(document)
                              }}
                              style={{ overflowWrap: 'anywhere' }}
                            >
                              {document.title || document.original_file_name || 'Untitled'}
                            </button>
                            <PreviewPopup document={document}>
                              <i
                                className="bi bi-eye preview-button ms-1"
                                title={t('preview_document')}
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => e.stopPropagation()}
                              ></i>
                            </PreviewPopup>
                          </div>
                        </td>
                      )}
                      {displayFields.includes(DisplayFieldEnum.TAGS) && (
                        <td>
                          {document.tags && document.tags.length > 0 && (
                            <div className="d-flex flex-wrap gap-1">
                              {document.tags.map((tagId: number) =>
                                (() => {
                                  const found = (document.tags_display || []).find((tag: any) => tag.id === tagId)
                                  const tagDisplay: TagType | undefined = found
                                    ? {
                                        id: found.id,
                                        name: found.name,
                                        color: found.color || '#6c757d',
                                        text_color: found.text_color || '#ffffff',
                                        parent: found.parent ?? null,
                                        is_inbox_tag: false,
                                        matching_algorithm: 1,
                                        match: '',
                                        is_insensitive: true,
                                      }
                                    : undefined
                                  return (
                                    <Tag
                                      key={tagId}
                                      tag={tagDisplay}
                                      tagID={tagId}
                                      clickable={true}
                                      linkTitle={t('filter_by_tag')}
                                      onClick={(id) => {
                                        clickTag(id)
                                      }}
                                    />
                                  )
                                })()
                              )}
                            </div>
                          )}
                        </td>
                      )}
                      {displayFields.includes(DisplayFieldEnum.ASN) && <td>{document.archive_serial_number}</td>}
                      {displayFields.includes(DisplayFieldEnum.CORRESPONDENT) && (
                        <td>
                          {document.correspondent && (
                            <a
                              className="btn btn-link p-0 text-start"
                              onClick={(e) => {
                                e.stopPropagation()
                                const correspondent = document.correspondent
                                if (!correspondent) return
                                const correspondentId = typeof correspondent === 'object' ? correspondent.id : correspondent
                                clickCorrespondent(correspondentId)
                              }}
                              title={t('filter_by_correspondent')}
                              style={{ cursor: 'pointer' }}
                            >
                              {getCorrespondentName(document.correspondent, document.correspondent_display)}
                            </a>
                          )}
                        </td>
                      )}
                      {displayFields.includes(DisplayFieldEnum.OWNER) && (
                        <td>{getDocumentOwnerDisplayName(document, usersCache, usersCacheLoaded)}</td>
                      )}
                      {displayFields.includes(DisplayFieldEnum.NOTES) && (
                        <td>
                          {document.notes && document.notes.length > 0 && (
                            <a href={`/documents/${document.id}/notes`} className="btn btn-sm p-0" onClick={(e) => e.stopPropagation()}>
                              <span className="badge rounded-pill bg-light border text-primary">
                                <i className="bi bi-chat-left-text ms-1 me-1" style={{ width: '1.2em', height: '1.2em' }}></i>
                                {document.notes.length}
                              </span>
                            </a>
                          )}
                        </td>
                      )}
                      {displayFields.includes(DisplayFieldEnum.DOCUMENT_TYPE) && (
                        <td>
                          {document.document_type && (
                            <a
                              className="btn btn-link p-0 text-start"
                              onClick={(e) => {
                                e.stopPropagation()
                                const documentType = document.document_type
                                if (!documentType) return
                                const documentTypeId = typeof documentType === 'object' ? documentType.id : documentType
                                clickDocumentType(documentTypeId)
                              }}
                              title={t('filter_by_document_type')}
                              style={{ cursor: 'pointer' }}
                            >
                              {getDocumentTypeName(document.document_type, document.document_type_display)}
                            </a>
                          )}
                        </td>
                      )}
                      {displayFields.includes(DisplayFieldEnum.WORKFLOW_STATUS) && (
                        <td>
                          {document.workflow_status && (
                            <a
                              className="btn btn-link p-0 text-start text-decoration-none"
                              onClick={(e) => {
                                e.stopPropagation()
                                const ws = document.workflow_status
                                if (!ws) return
                                const id = typeof ws === 'object' ? ws.id : ws
                                clickWorkflowStatus(id)
                              }}
                              title={t('filter_by_workflow_status')}
                              style={{ cursor: 'pointer' }}
                            >
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: (document.workflow_status as any)?.color || '#6c757d',
                                  color: (document.workflow_status as any)?.text_color || '#fff',
                                }}
                              >
                                {getWorkflowStatusName(document.workflow_status)}
                              </span>
                            </a>
                          )}
                        </td>
                      )}
                      {displayFields.includes(DisplayFieldEnum.STORAGE_PATH) && (
                        <td>
                          {document.storage_path && (
                            <a
                              className="btn btn-link p-0 text-start"
                              onClick={(e) => {
                                e.stopPropagation()
                                const storagePath = document.storage_path
                                if (!storagePath) return
                                const storagePathId = typeof storagePath === 'object' ? storagePath.id : storagePath
                                clickStoragePath(storagePathId)
                              }}
                              title={t('filter_by_storage_path')}
                              style={{ cursor: 'pointer' }}
                            >
                              {getStoragePathName(document.storage_path, document.storage_path_display)}
                            </a>
                          )}
                        </td>
                      )}
                      {displayFields.includes(DisplayFieldEnum.CREATED) && <td>{formatDate(document.created)}</td>}
                      {displayFields.includes(DisplayFieldEnum.ADDED) && <td>{formatDate(document.added)}</td>}
                      {displayFields.includes(DisplayFieldEnum.PAGE_COUNT) && <td>{document.page_count}</td>}
                      {displayFields.includes(DisplayFieldEnum.SHARED) && <td>{document.is_shared_by_requester ? t('yes') : t('no')}</td>}
                      {activeDisplayCustomFields.map((fieldId) => {
                        const fieldIdNum = parseInt(fieldId.replace(DisplayFieldEnum.CUSTOM_FIELD, ''))
                        const customFieldInstance = document.custom_fields?.find((cf: any) => cf.field === fieldIdNum)
                        const fieldDef = customFields.find((f) => f.id === fieldIdNum)
                        const displayValue = customFieldInstance ? formatCustomFieldDisplayValue(customFieldInstance, fieldDef) : ''
                        return <td key={fieldId}>{displayValue ? <span>{displayValue}</span> : <span>-</span>}</td>
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isHierarchyView && documents.length > 15 && <div className="mt-3">{renderPagination()}</div>}
        </div>
      </div>

      {isMobileFiltersOpen && <div className="documents-filters-backdrop d-lg-none" onClick={closeMobileFilters}></div>}

      {/* Save View Config Dialog */}
      <SaveViewConfigDialog
        show={showSaveViewDialog}
        onHide={() => setShowSaveViewDialog(false)}
        onSave={(config) => handleSaveView(config)}
        defaultName={activeSavedViewTitle || t('current_view')}
        error={saveViewError}
      />

      {/* Documentation Dialog */}
      <DocumentationDialog show={showDocsDialog} onHide={() => setShowDocsDialog(false)} docKey="document_list_toolbar" />

      <ConfirmDialog
        show={showDeleteDialog}
        title={t('confirm_delete')}
        message={
          t('confirm_move_to_trash', { count: 1 }) ||
          t('confirm_delete_document', { title: documentToDelete?.title || documentToDelete?.original_file_name || 'Untitled' })
        }
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (isDeleteSubmitting) return
          setShowDeleteDialog(false)
          setDocumentToDelete(null)
        }}
        variant="danger"
        showPermanentDeleteOption={true}
        permanentDeleteLabel={t('permanent_delete')}
        permanentDeleteDescription={t('permanent_delete_description')}
        isConfirming={isDeleteSubmitting}
      />

      <CreateDocumentDialog
        show={showCreateDocumentDialog}
        onHide={() => setShowCreateDocumentDialog(false)}
        onCreate={handleCreateDocumentMetadata}
      />
    </div>
  )
}

export default DocumentList
